/**
 * React hook for fetching inspection schemas with offline support
 */

import { useEffect, useState, useCallback } from 'react';
import { useOffline } from './useOffline';
import { offlineStorage } from '../services/offlineStorage';
import { inspectionSchemaService } from '../services/inspectionSchemaService';
import type { InspectionSchemaDetailResponse } from '../types/inspectionSchema';

export interface UseInspectionSchemaResult {
  schema: InspectionSchemaDetailResponse | null;
  isLoading: boolean;
  error: Error | null;
  isFromCache: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch and cache inspection schema for a site.
 * - When online: fetches fresh schema and caches it
 * - When offline: returns cached schema if available
 * - Caches schemas in IndexedDB for offline access
 */
export function useInspectionSchema(
  siteId: string | undefined
): UseInspectionSchemaResult {
  const { isOnline } = useOffline();
  const [schema, setSchema] = useState<InspectionSchemaDetailResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  const fetchSchema = useCallback(async () => {
    if (!siteId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Wait for IDB to be ready — main.tsx kicks off init() but React can
      // render this hook before that promise resolves. Memoized, so this is
      // a no-op after the first successful open.
      await offlineStorage.init();

      // Always try to get cached schema first for fast initial load
      const cached = await offlineStorage.getCachedSchema(siteId);

      if (cached) {
        setSchema(cached.schema);
        setIsFromCache(true);
      }

      // If online, fetch fresh data and update cache
      if (isOnline) {
        try {
          const freshSchema =
            await inspectionSchemaService.resolveSchemaForSite(siteId);
          setSchema(freshSchema);
          setIsFromCache(false);

          // Cache the fresh schema
          await offlineStorage.cacheSchemaForSite(siteId, freshSchema);
        } catch (fetchError) {
          // If online fetch fails but we have cache, use it
          if (cached) {
            console.warn(
              'Failed to fetch fresh schema, using cached version:',
              fetchError
            );
            setSchema(cached.schema);
            setIsFromCache(true);
          } else {
            throw fetchError;
          }
        }
      } else if (!cached) {
        // Offline and no cache - this is an error
        throw new Error(
          'No cached schema available. Please connect to the internet to load the inspection form.'
        );
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching inspection schema:', err);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, isOnline]);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  const refresh = useCallback(async () => {
    if (isOnline) {
      await fetchSchema();
    }
  }, [fetchSchema, isOnline]);

  return {
    schema,
    isLoading,
    error,
    isFromCache,
    refresh,
  };
}
